import { invalidateInventory } from '../collection/inventoryInvalidation';
import { Item } from '../../domain/item';
import { InventoryAuthority } from '@qkindex/contracts';
import { useRef } from 'react';
import { createOperationId } from '../../domain/operationId';
import {
  bulkOperationService,
  MAX_DIRECT_BULK_ITEMS,
} from '../../services/bulkOperationService';
import { itemService } from '../../services/itemService';
import { logger } from '../../services/logger';
import { useFeedback } from '../feedback/FeedbackProvider';

export function useBinActions({
  authority,
  permanentDeleteAuthority,
  closeBin,
  setLastUsedLocation,
}: {
  authority?: InventoryAuthority;
  permanentDeleteAuthority?: InventoryAuthority;
  closeBin: () => void;
  setLastUsedLocation: (location: string) => Promise<void> | void;
}) {
  const { notify, confirm } = useFeedback();
  const pendingDeletion = useRef<
    { key: string; operationId: string } | undefined
  >(undefined);

  const restore = async (item: Item) => {
    if (!authority) return false;
    try {
      await itemService.restoreItem(authority, item.id);
      invalidateInventory({
        ...authority,
        resources: ['items'],
        reloadItemIds: [item.id],
      });
      await setLastUsedLocation(item.location);
      return true;
    } catch (error) {
      logger.error('The item could not be restored.', error);
      notify('The item could not be restored.', 'error');
      return false;
    }
  };

  const deleteItems = async (items: Item[]): Promise<string[]> => {
    if (
      !permanentDeleteAuthority ||
      !items.length ||
      items.length > MAX_DIRECT_BULK_ITEMS
    )
      return [];
    if (
      !(await confirm(
        `Permanently delete ${items.length} item(s)? This action cannot be undone.`,
      ))
    )
      return [];
    const itemIds = items.map((item) => item.id);
    const key = JSON.stringify({
      collectionId: permanentDeleteAuthority.collectionId,
      itemIds: [...itemIds].sort(),
    });
    if (pendingDeletion.current?.key !== key) {
      pendingDeletion.current = { key, operationId: createOperationId() };
    }
    try {
      await bulkOperationService.deleteItems(
        permanentDeleteAuthority,
        itemIds,
        pendingDeletion.current.operationId,
      );
      invalidateInventory({
        ...permanentDeleteAuthority,
        resources: ['items'],
        deletedItemIds: itemIds,
      });
      pendingDeletion.current = undefined;
      return itemIds;
    } catch (error) {
      logger.error('The items could not be deleted.', error);
      notify('The items could not be deleted. Try again.', 'error');
      return [];
    }
  };

  return {
    restore,
    deleteItems,
    async restoreItems(items: Item[]) {
      if (items.length > MAX_DIRECT_BULK_ITEMS) return [];
      const restored: string[] = [];
      for (const item of items) {
        if (await restore(item)) restored.push(item.id);
      }
      if (restored.length !== items.length)
        notify(
          `${restored.length} of ${items.length} item(s) restored.`,
          'error',
        );
      return restored;
    },
    async empty(deletedItems: Item[]) {
      if (!permanentDeleteAuthority || deletedItems.length === 0) return false;
      const batch = deletedItems.slice(0, MAX_DIRECT_BULK_ITEMS);
      const remaining = deletedItems.length - batch.length;
      const deleted = await deleteItems(batch);
      if (!deleted.length) return false;
      if (remaining === 0) {
        closeBin();
      }
      return true;
    },
  };
}
